import React from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ReportSheetFooter } from '../../../components/ui/Report';
import type { MgaCrewLine, MgaPeriod } from '../useData';
import { formatReportMoney } from './reportMeta';

function isNonZero(n: number | null | undefined) {
  return Number.isFinite(Number(n)) && Math.abs(Number(n)) > 0.0001;
}

type PeriodHeader = Pick<
  MgaPeriod,
  'id' | 'vesselId' | 'vessel' | 'periodFrom' | 'periodTo' | 'currency'
>;

export type PayslipDocumentProps = {
  period: PeriodHeader;
  line: MgaCrewLine;
  /** Extra class on the root article (e.g. page-break helpers). */
  className?: string;
  /** When true, omit fixed A4 height so bulk packs can flow across pages. */
  compact?: boolean;
  /** Include `app-print-root` (default true). Bulk packs wrap once at the outer level. */
  printRoot?: boolean;
  /** Management company logo (authenticated blob URL). */
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
  pageNumber?: number;
  pageCount?: number;
};

function LineRow({
  label,
  value,
  emph,
  deduct,
}: {
  label: string;
  value: React.ReactNode;
  emph?: boolean;
  deduct?: boolean;
}) {
  return (
    <tr className={cn(emph && 'mga-payslip-emph')}>
      <td className="mga-payslip-label">{deduct ? `− ${label}` : label}</td>
      <td className="mga-payslip-amt">{value}</td>
    </tr>
  );
}

/** Shared monthly pay account markup (single page or bulk). */
export const PayslipDocument: React.FC<PayslipDocumentProps> = ({
  period,
  line,
  className,
  compact = false,
  printRoot = true,
  companyLogoSrc = null,
  companyLogoAlt = 'Management company logo',
  pageNumber = 1,
  pageCount = 1,
}) => {
  const ccy = period.currency || 'USD';
  const otTotal =
    line.otTotal ?? (Number(line.overtimeHours) || 0) * (Number(line.overtimeRate) || 0);
  const earnings = (line.wageValues ?? []).filter(
    (v) => v.columnType !== 'DEDUCTION' && v.columnType !== 'INFO' && v.includeInTotal,
  );
  const wageDeds = (line.wageValues ?? []).filter(
    (v) => v.columnType === 'DEDUCTION' && v.includeInTotal && isNonZero(v.periodAmount),
  );
  const structuredDeds: Array<{ label: string; value: number }> = [
    { label: 'Cash advance (office)', value: Number(line.cashAdvanceOffice) || 0 },
    { label: 'Cash advance (master)', value: Number(line.cashAdvanceMaster) || 0 },
    { label: 'Allotments', value: Number(line.allotmentAmount) || 0 },
    { label: 'Cigarettes', value: Number(line.cigarettesAmount) || 0 },
    { label: 'Internet cards', value: Number(line.internetCardsAmount) || 0 },
    { label: 'Other bonded', value: Number(line.otherBondedAmount) || 0 },
    { label: 'Various', value: Number(line.variousDeductions) || 0 },
  ].filter((d) => isNonZero(d.value));

  return (
    <article
      className={cn(
        printRoot && 'app-print-root',
        'mga-payslip',
        compact && 'mga-payslip-compact',
        className,
      )}
    >
      <header className="mga-payslip-header">
        <div className="mga-payslip-header-banner">
          <div className="mga-payslip-header-side mga-payslip-header-side-start">
            {companyLogoSrc ? (
              <img
                src={companyLogoSrc}
                alt={companyLogoAlt}
                className="mga-payslip-logo"
              />
            ) : null}
          </div>
          <div className="mga-payslip-header-center">
            <p className="mga-payslip-eyebrow">Master&apos;s general account</p>
            <h1>Monthly pay account</h1>
            <p className="mga-payslip-vessel">
              M.V. {period.vessel?.vesselName ?? `#${period.vesselId}`}
            </p>
          </div>
          <div className="mga-payslip-header-side mga-payslip-header-side-end" aria-hidden />
        </div>
        <table className="mga-payslip-header-bar">
          <tbody>
            <tr>
              <td>
                <span className="mga-payslip-bar-label">Period</span>
                <span className="mga-payslip-bar-value">
                  {formatDateGlobal(period.periodFrom)} – {formatDateGlobal(period.periodTo)}
                </span>
              </td>
              <td>
                <span className="mga-payslip-bar-label">Currency</span>
                <span className="mga-payslip-bar-value">{ccy}</span>
              </td>
              <td>
                <span className="mga-payslip-bar-label">Sheet</span>
                <span className="mga-payslip-bar-value">No. {period.id}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </header>

      <table className="mga-payslip-id">
        <tbody>
          <tr>
            <th>Name</th>
            <td>{line.crewName}</td>
            <th>Rank</th>
            <td>{line.rankName || '—'}</td>
          </tr>
          <tr>
            <th>Sign on</th>
            <td>{line.signOnDate ? formatDateGlobal(line.signOnDate) : '—'}</td>
            <th>Days</th>
            <td>{line.daysInPeriod}</td>
          </tr>
        </tbody>
      </table>

      <div className="mga-payslip-stack">
        <section>
          <h2>Income</h2>
          <table className="mga-payslip-ledger">
            <tbody>
              {earnings.map((v) => (
                <LineRow
                  key={v.id}
                  label={v.columnName || v.columnCode}
                  value={formatReportMoney(v.periodAmount)}
                />
              ))}
              {isNonZero(line.extraEarnings) && (
                <LineRow label="Extra earnings" value={formatReportMoney(line.extraEarnings)} />
              )}
              {(isNonZero(otTotal) || isNonZero(line.overtimeHours)) && (
                <LineRow
                  label={`Overtime (${line.overtimeHours || 0} h × ${formatReportMoney(line.overtimeRate)})`}
                  value={formatReportMoney(otTotal)}
                />
              )}
              {isNonZero(line.previousBalance) && (
                <LineRow
                  label="Previous balance"
                  value={formatReportMoney(line.previousBalance)}
                />
              )}
              <LineRow label="Total income" value={formatReportMoney(line.grossPayable)} emph />
            </tbody>
          </table>
        </section>

        <section>
          <h2>Deductions</h2>
          <table className="mga-payslip-ledger">
            <tbody>
              {wageDeds.map((v) => (
                <LineRow
                  key={v.id}
                  label={v.columnName || v.columnCode}
                  value={formatReportMoney(v.periodAmount)}
                  deduct
                />
              ))}
              {structuredDeds.map((d) => (
                <LineRow
                  key={d.label}
                  label={d.label}
                  value={formatReportMoney(d.value)}
                  deduct
                />
              ))}
              {!wageDeds.length && !structuredDeds.length && (
                <tr>
                  <td className="mga-payslip-label mga-payslip-muted" colSpan={2}>
                    None
                  </td>
                </tr>
              )}
              <LineRow
                label="Total deductions"
                value={formatReportMoney(line.totalDeductions ?? line.deductions)}
                emph
                deduct
              />
            </tbody>
          </table>
        </section>
      </div>

      <section className="mga-payslip-balance">
        {line.remarks ? <div className="mga-payslip-remarks">Remarks: {line.remarks}</div> : null}
        <div className="mga-payslip-balance-main">
          <div className="mga-payslip-balance-label">Balance due to crew ({ccy})</div>
          <div className="mga-payslip-balance-value">{formatReportMoney(line.netDue)}</div>
        </div>
      </section>

      <footer className="mga-payslip-sign">
        <div>
          <div className="mga-payslip-sign-line" />
          <div className="mga-payslip-sign-title">Receiver / Crew</div>
          <div className="mga-payslip-muted">Name &amp; signature</div>
        </div>
        <div>
          <div className="mga-payslip-sign-line" />
          <div className="mga-payslip-sign-title">Master</div>
          <div className="mga-payslip-muted">Name &amp; signature</div>
        </div>
      </footer>
      <ReportSheetFooter pageNumber={pageNumber} pageCount={pageCount} />
    </article>
  );
};

export default PayslipDocument;
